import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Send } from "lucide-react";
import { useLang } from "@/i18n";
import { useBooking } from "@/BookingContext";
import Reveal from "@/components/Reveal";

const BLOCKS = [
  { key: "coach", imgKey: "coach" },
  { key: "masseuse", imgKey: "spa" },
  { key: "coiffeur", imgKey: "coiffeur" },
  { key: "chauffeur", imgKey: "chauffeur" },
  { key: "cuisinier", imgKey: "chefPlating" },
  { key: "babysitter", imgKey: "babysitter" },
];

export default function ProvidersPage() {
  const { t, simg } = useLang();
  const { openBooking } = useBooking();
  const location = useLocation();
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    if (location.hash) {
      const key = location.hash.slice(1);
      if (BLOCKS.some((b) => b.key === key)) setOpenKey(key);
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [location.hash]);

  return (
    <main data-testid="providers-page">
      <header className="relative flex h-[55vh] min-h-[420px] items-end overflow-hidden">
        <img src={simg("redRocks")} alt="Calanques de Corse-du-Sud" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-mer/80 via-mer/30 to-mer/40" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 text-casse">
          <p className="eyebrow text-sable">{t.providers.eyebrow}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl">{t.providers.title}</h1>
          <p className="mt-4 max-w-xl text-sm sm:text-base text-casse/85">{t.providers.sub}</p>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-10 sm:py-14">
        {BLOCKS.map(({ key, imgKey }, i) => {
          const open = openKey === key;
          return (
            <section key={key} id={key} data-testid={`provider-block-${key}`} className="border-b border-maquis-deep/10 last:border-b-0">
              <button
                data-testid={`provider-accordion-${key}`}
                onClick={() => setOpenKey(open ? null : key)}
                className="flex w-full items-center justify-between gap-6 py-8 sm:py-10 text-left"
              >
                <div className="flex items-baseline gap-5 sm:gap-8">
                  <span className="eyebrow">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-2xl sm:text-4xl text-maquis-deep">{t.providers.items[key].title}</h2>
                </div>
                <ChevronDown
                  size={22}
                  strokeWidth={1.25}
                  className={`shrink-0 text-maquis-olive transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
              </button>
              <div className={`grid transition-all duration-500 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <div className="grid items-center gap-8 pb-12 lg:grid-cols-2 lg:gap-16">
                    <div className="overflow-hidden rounded-md">
                      <img
                        src={simg(imgKey)}
                        alt={t.providers.items[key].title}
                        loading="lazy"
                        className="h-64 w-full object-cover sm:h-[340px] transition-transform duration-[1.4s] ease-out hover:scale-105"
                      />
                    </div>
                    <div>
                      <p className="text-base leading-relaxed text-maquis-olive">{t.providers.items[key].desc}</p>
                      <button
                        data-testid={`provider-cta-${key}`}
                        onClick={() => openBooking({ providers: [key] })}
                        className="btn-outline-elegant mt-7 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                      >
                        {t.providers.select_cta}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section data-testid="providers-cta-section" className="border-t border-maquis-deep/10 bg-ivoire">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight text-maquis-deep">{t.providers.cta_title}</h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-maquis-olive">{t.providers.cta_text}</p>
            <Link
              to="/contact"
              data-testid="providers-cta-button"
              className="btn-outline-elegant mt-8 inline-flex text-maquis-deep hover:bg-maquis-deep hover:text-casse"
            >
              {t.providers.cta_button}
            </Link>
          </Reveal>
          <div
            className="relative mx-auto hidden h-[150px] w-[420px] max-w-full select-none sm:block"
            aria-hidden="true"
            data-testid="providers-plane-animation"
          >
            <svg viewBox="0 0 420 150" className="absolute inset-0 h-full w-full" fill="none">
              <path
                d="M10,128 C55,30 165,18 198,74 C224,120 170,158 138,116 C108,77 168,42 238,64 C308,86 360,60 408,26"
                stroke="#717F63"
                strokeWidth="1.5"
                strokeDasharray="1 8"
                strokeLinecap="round"
                opacity="0.7"
              />
            </svg>
            <div className="plane-orbit absolute left-0 top-0 text-maquis-olive">
              <Send size={26} strokeWidth={1.25} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
